/**
 * Erreurs métier du domaine
 *
 * Ces erreurs représentent des violations de règles métier
 * et non des erreurs techniques
 */

export class DomainError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'DomainError';
    Object.setPrototypeOf(this, DomainError.prototype);
  }
}

/**
 * Erreur: montant invalide (négatif ou zéro)
 */
export class InvalidAmountError extends DomainError {
  constructor(amount: number) {
    super(
      'INVALID_AMOUNT',
      `Le montant doit être positif (reçu: ${amount})`,
      { amount }
    );
    this.name = 'InvalidAmountError';
  }
}

/**
 * Erreur: solde insuffisant pour effectuer l'opération
 */
export class InsufficientBalanceError extends DomainError {
  constructor(available: number, requested: number) {
    super(
      'INSUFFICIENT_BALANCE',
      `Solde insuffisant. Disponible: ${available}, Demandé: ${requested}`,
      { available, requested }
    );
    this.name = 'InsufficientBalanceError';
  }
}

/**
 * Erreur: billets insuffisants en caisse
 */
export class InsufficientCashError extends DomainError {
  constructor(message: string) {
    super('INSUFFICIENT_CASH', message);
    this.name = 'InsufficientCashError';
  }
}

/**
 * Erreur: compte bloqué ou fermé
 */
export class AccountBlockedError extends DomainError {
  constructor(accountNumber: string, status: string) {
    super(
      'ACCOUNT_BLOCKED',
      `Le compte ${accountNumber} est ${status}`,
      { accountNumber, status }
    );
    this.name = 'AccountBlockedError';
  }
}

/**
 * Erreur: client non autorisé sur le compte
 */
export class UnauthorizedClientError extends DomainError {
  constructor(clientId: string, accountNumber: string) {
    super(
      'UNAUTHORIZED_CLIENT',
      `Le client ${clientId} n'est pas autorisé sur le compte ${accountNumber}`,
      { clientId, accountNumber }
    );
    this.name = 'UnauthorizedClientError';
  }
}

/**
 * Erreur: authentification biométrique échouée
 */
export class BiometricAuthFailedError extends DomainError {
  constructor(reason?: string) {
    super(
      'BIOMETRIC_AUTH_FAILED',
      reason || 'Échec de l\'authentification biométrique',
      { reason }
    );
    this.name = 'BiometricAuthFailedError';
  }
}

/**
 * Erreur: pièce d'identité invalide ou expirée
 */
export class InvalidIdentityDocumentError extends DomainError {
  constructor(reason: string) {
    super('INVALID_IDENTITY_DOCUMENT', reason);
    this.name = 'InvalidIdentityDocumentError';
  }
}

/**
 * Erreur: transition d'état invalide dans le workflow
 */
export class InvalidStateTransitionError extends DomainError {
  constructor(from: string, to: string) {
    super(
      'INVALID_STATE_TRANSITION',
      `Transition invalide de ${from} vers ${to}`,
      { from, to }
    );
    this.name = 'InvalidStateTransitionError';
  }
}

/**
 * Erreur: dénomination invalide (billetage incohérent)
 */
export class InvalidDenominationError extends DomainError {
  constructor(message: string) {
    super('INVALID_DENOMINATION', message);
    this.name = 'InvalidDenominationError';
  }
}

/**
 * Erreur: transaction ne peut pas être modifiée (immutabilité)
 */
export class TransactionImmutableError extends DomainError {
  constructor(transactionId: string) {
    super(
      'TRANSACTION_IMMUTABLE',
      `La transaction ${transactionId} ne peut pas être modifiée`,
      { transactionId }
    );
    this.name = 'TransactionImmutableError';
  }
}

/**
 * Erreur: entité non trouvée
 */
export class EntityNotFoundError extends DomainError {
  constructor(entityType: string, id: string) {
    super(
      'ENTITY_NOT_FOUND',
      `${entityType} avec l'identifiant ${id} introuvable`,
      { entityType, id }
    );
    this.name = 'EntityNotFoundError';
  }
}
